import axios from "axios";
import { inject, injectable } from "inversify";
import { ITeamRepository } from "@domain/repositories";
import type {
  CreateTeam,
  EffectiveSoftwareVersionContextConfiguration,
  EffectiveStorageContextConfiguration,
  PathTypeSchema,
  PathVisibilitySchema,
  SoftwareEnumSchema,
  SoftwareVersionContextConfiguration,
  StorageContextConfiguration,
} from "@domain/schemas/BI";
import type { MyTeam, TeamDetail, TeamProjectInventoryItem, TeamRoleName } from "@domain/schemas/workspace";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";

// Implementation of the ITeamRepository interface
@injectable()
export class TeamRepository implements ITeamRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getMyTeamsAsync(): Promise<MyTeam[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teams}`;
    return this._requestHandlerService.handleRequestAsync<MyTeam[]>(() => axios.get<MyTeam[]>(url));
  }

  public async getTeamProjectInventoryAsync(teamId: string): Promise<TeamProjectInventoryItem[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamProjectInventory}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<TeamProjectInventoryItem[]>(() => axios.get<TeamProjectInventoryItem[]>(url));
  }

  public async getEffectiveSoftwareVersionConfigurationAsync(teamId: string): Promise<EffectiveSoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamSoftwareVersionConfigurationEffective}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<EffectiveSoftwareVersionContextConfiguration>(() => axios.get<EffectiveSoftwareVersionContextConfiguration>(url));
  }

  public async getSoftwareVersionConfigurationAsync(teamId: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamSoftwareVersionConfiguration}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.get<SoftwareVersionContextConfiguration>(url));
  }

  public async addSoftwareVersionAsync(teamId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamSoftwareVersionConfigurationVersions}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.post<SoftwareVersionContextConfiguration>(url, { software, version }));
  }

  public async removeSoftwareVersionAsync(teamId: string, versionId: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamSoftwareVersionConfigurationVersionById}`.replace("{teamId}", () => teamId).replace("{versionId}", () => versionId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.delete<SoftwareVersionContextConfiguration>(url));
  }

  public async setSoftwareVersionDefaultAsync(teamId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamSoftwareVersionConfigurationDefault}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.put<SoftwareVersionContextConfiguration>(url, { software, version }));
  }

  public async clearSoftwareVersionDefaultAsync(teamId: string, software: SoftwareEnumSchema): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamSoftwareVersionConfigurationDefaultForSoftware}`.replace("{teamId}", () => teamId).replace("{software}", () => software);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.delete<SoftwareVersionContextConfiguration>(url));
  }

  public async getEffectiveStorageConfigurationAsync(teamId: string): Promise<EffectiveStorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfigurationEffective}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<EffectiveStorageContextConfiguration>(() => axios.get<EffectiveStorageContextConfiguration>(url));
  }

  public async getStorageConfigurationAsync(teamId: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfiguration}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.get<StorageContextConfiguration>(url));
  }

  public async addStoragePathAsync(teamId: string, pathType: PathTypeSchema, path: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfigurationPaths}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.post<StorageContextConfiguration>(url, { pathType, path, visibility }));
  }

  public async removeStoragePathAsync(teamId: string, pathId: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfigurationPathById}`.replace("{teamId}", () => teamId).replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.delete<StorageContextConfiguration>(url));
  }

  public async setStoragePathVisibilityAsync(teamId: string, pathId: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfigurationPathVisibility}`.replace("{teamId}", () => teamId).replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { visibility }));
  }

  public async setStoragePathValueAsync(teamId: string, pathId: string, path: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfigurationPathValue}`.replace("{teamId}", () => teamId).replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { path }));
  }

  public async setStorageResultsPathAsync(teamId: string, resultsPath: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamStorageConfigurationResultsPath}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { resultsPath }));
  }

  public async getTeamAsync(teamId: string): Promise<TeamDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamById}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<TeamDetail>(() => axios.get<TeamDetail>(url));
  }

  public async createTeamAsync(request: CreateTeam): Promise<TeamDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teams}`;
    return this._requestHandlerService.handleRequestAsync<TeamDetail>(() => axios.post<TeamDetail>(url, request));
  }

  public async deleteTeamAsync(teamId: string): Promise<void> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamById}`.replace("{teamId}", () => teamId);
    await this._requestHandlerService.handleRequestAsync<boolean>(() => axios.delete<boolean>(url));
  }

  public async renameTeamAsync(teamId: string, newName: string): Promise<TeamDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamName}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<TeamDetail>(() => axios.put<TeamDetail>(url, { newName }));
  }

  public async assignTeamRoleAsync(teamId: string, targetUserId: string, role: TeamRoleName): Promise<TeamDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamMembers}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<TeamDetail>(() => axios.put<TeamDetail>(url, { targetUserId, role }));
  }

  public async removeTeamMemberAsync(teamId: string, userId: string, shouldAlsoRemoveProjectRolesInTeamProjects: boolean): Promise<TeamDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamMemberById}`.replace("{teamId}", () => teamId).replace("{userId}", () => userId);
    const parameters = new URLSearchParams({ alsoRemoveProjectRolesInTeamProjects: String(shouldAlsoRemoveProjectRolesInTeamProjects) });
    return this._requestHandlerService.handleRequestAsync<TeamDetail>(() => axios.delete<TeamDetail>(`${url}?${parameters.toString()}`));
  }
}
